import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FaSearch, FaCheckDouble, FaEllipsisV, FaTrash, FaEdit,
  FaTruck, FaPlus
} from 'react-icons/fa';
import { RiDeleteBin5Fill, RiBox3Fill } from "react-icons/ri";
import { IoMdArrowDropdown } from "react-icons/io";
import { IoSettingsSharp } from "react-icons/io5";
import { FaCircleUser } from "react-icons/fa6";
import Swal from 'sweetalert2';
import axiosInstance from '../../utils/axiosConfig';
import './Suppliers.css';

const API_BASE = 'https://super-backend.hira-tech.co.uk/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  const shopId =
    localStorage.getItem('selectedShopId') ||
    localStorage.getItem('selected_shop_id') ||
    '';
  return {
    Authorization: token ? `Bearer ${token}` : '',
    'X-Shop-Id': shopId,
    Accept: 'application/json',
  };
};

const Suppliers = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [suppliersData, setSuppliersData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [showActionDropdown, setShowActionDropdown] = useState(false);

  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState('');
  const [loadingCities, setLoadingCities] = useState(false);

  const [shops, setShops] = useState([]);
  const [loadingShops, setLoadingShops] = useState(false);
  const [modalCities, setModalCities] = useState([]);
  const [loadingModalCities, setLoadingModalCities] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentSupplierId, setCurrentSupplierId] = useState(null);
  const [displayNameManuallyEdited, setDisplayNameManuallyEdited] = useState(false);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    title: '', firstName: '', middleName: '', lastName: '', suffix: '',
    companyName: '', supplierDisplayName: '', email: '', phoneNumber: '',
    cc: '', bcc: '', mobileNumber: '', fax: '', other: '',
    streetAddress: '', shop: '', city: '', country: '', postcode: '',
    state: '', balance: '0.00',
  });

  const [currentShopId, setCurrentShopId] = useState(null);

  /* -------------------------------------------------------------------------
     Shop ID from URL (same as before)
     ------------------------------------------------------------------------- */
  useEffect(() => {
    const match = location.pathname.match(/\/shop\/(\d+)/);
    if (match) {
      setCurrentShopId(parseInt(match[1], 10));
    } else {
      const params = new URLSearchParams(location.search);
      const shopId = params.get('shop_id');
      setCurrentShopId(shopId ? parseInt(shopId, 10) : null);
    }
  }, [location.pathname, location.search]);

  /* -------------------------------------------------------------------------
     API: Fetch suppliers
     GET /api/suppliers/  (optional ?city=...)
     ------------------------------------------------------------------------- */
  const fetchSuppliers = useCallback(async (cityFilter = null) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (cityFilter) params.append('city', cityFilter);

      const url = `/api/suppliers/${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await axiosInstance.get(url);

      // DRF may return {results: [...]} or plain array
      const rawData = Array.isArray(response.data)
        ? response.data
        : response.data.results || [];

      // LocalStorage filter (kept from your original code)
      const deletedIds = JSON.parse(localStorage.getItem('deleted_suppliers') || '[]');
      const filtered = rawData.filter((item) => !deletedIds.includes(item.id));

      const mappedData = filtered.map((item) => ({
        ...item,
        supplier: item.supplier || item.supplier_display_name || 'N/A',
        companyName: item.company_name || item.companyName || 'N/A',
        phone: item.phone_number || item.phone || item.mobile_number || 'N/A',
        email: item.email || 'N/A',
        city: item.city || 'N/A',
        openBalance: parseFloat(item.open_balance ?? item.openBalance ?? 0),
      }));

      setSuppliersData(mappedData);
    } catch (err) {
      console.error('Error fetching suppliers:', err?.response?.data || err);
      setSuppliersData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  /* -------------------------------------------------------------------------
     API: Cities for top dropdown
     GET /api/expenses/shop/<id>/cities/
     ------------------------------------------------------------------------- */
  const fetchShopCities = async (shopId) => {
    if (!shopId) return;
    try {
      setLoadingCities(true);
      const res = await fetch(
        `${API_BASE}/expenses/shop/${shopId}/cities/`,
        { headers: getAuthHeaders() }
      );
      if (res.ok) {
        const data = await res.json();
        const cityNames = (Array.isArray(data) ? data : data.results || [])
          .map((c) => (typeof c === 'string' ? c : c.name))
          .filter(Boolean);
        setCities(cityNames);
      }
    } catch (err) {
      console.error('Error fetching cities:', err);
    } finally {
      setLoadingCities(false);
    }
  };

  /* -------------------------------------------------------------------------
     API: Shops for modal
     GET /api/expenses/shops/
     ------------------------------------------------------------------------- */
  const fetchShops = async () => {
    setLoadingShops(true);
    try {
      const res = await fetch(`${API_BASE}/expenses/shops/`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setShops(Array.isArray(data) ? data : data.results || []);
      }
    } catch (err) {
      console.error('Shops fetch error:', err);
    } finally {
      setLoadingShops(false);
    }
  };

  /* -------------------------------------------------------------------------
     API: Cities for modal (depends on selected shop)
     GET /api/expenses/shop/<id>/cities/
     ------------------------------------------------------------------------- */
  const fetchModalCitiesForShop = async (shopId) => {
    if (!shopId) {
      setModalCities([]);
      return;
    }
    setLoadingModalCities(true);
    try {
      const res = await fetch(
        `${API_BASE}/expenses/shop/${shopId}/cities/`,
        { headers: getAuthHeaders() }
      );
      if (res.ok) {
        const data = await res.json();
        setModalCities(Array.isArray(data) ? data : data.results || []);
      }
    } catch (err) {
      console.error('Modal cities fetch error:', err);
    } finally {
      setLoadingModalCities(false);
    }
  };

  /* -------------------------------------------------------------------------
     Effects
     ------------------------------------------------------------------------- */
  useEffect(() => {
    fetchSuppliers(selectedCity);
  }, [selectedCity, fetchSuppliers]);

  useEffect(() => {
    if (currentShopId) fetchShopCities(currentShopId);
  }, [currentShopId]);

  useEffect(() => {
    fetchShops();
  }, []);

  /* -------------------------------------------------------------------------
     Handlers
     ------------------------------------------------------------------------- */
  const handleCityChange = (e) => {
    setSelectedCity(e.target.value);
    setSelectedSuppliers([]);
  };

  const filteredSuppliers = suppliersData.filter((supplier) => {
    const name = (supplier.supplier || '').toLowerCase();
    const email = (supplier.email || '').toLowerCase();
    const term = searchTerm.toLowerCase();
    return name.includes(term) || email.includes(term);
  });

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount || 0);

  const totalSuppliers = suppliersData.length;
  const totalOpenBalance = suppliersData.reduce(
    (sum, s) => sum + (s.openBalance || 0),
    0
  );
  const totalOverdue = 8087.27;
  const totalPaid = 3985.69;

  const toggleSupplierSelection = (supplier) => {
    setSelectedSuppliers((prev) => {
      const exists = prev.some((s) => s.id === supplier.id);
      return exists
        ? prev.filter((s) => s.id !== supplier.id)
        : [...prev, supplier];
    });
  };

  const handleSelectAll = () => {
    if (
      selectedSuppliers.length === filteredSuppliers.length &&
      filteredSuppliers.length > 0
    ) {
      setSelectedSuppliers([]);
    } else {
      setSelectedSuppliers([...filteredSuppliers]);
    }
  };

  /* -------------------------------------------------------------------------
     DELETE: single
     DELETE /api/suppliers/<id>/
     ------------------------------------------------------------------------- */
  const handleDeleteSupplier = async (supplier) => {
    const result = await Swal.fire({
      title: 'Delete Supplier?',
      html: `Are you sure you want to delete <strong>${supplier.supplier}</strong>?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#004aad',
      confirmButtonText: 'Yes, Delete',
    });

    if (!result.isConfirmed) return;

    try {
      await axiosInstance.delete(`/api/suppliers/${supplier.id}/`, {
        headers: getAuthHeaders(),
      });

      setSuppliersData((prev) => prev.filter((s) => s.id !== supplier.id));
      setSelectedSuppliers((prev) => prev.filter((s) => s.id !== supplier.id));

      Swal.fire({
        icon: 'success',
        title: 'Deleted!',
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error('Delete supplier error:', err?.response?.data || err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Could not delete supplier. Please try again.',
      });
    }
  };

  /* -------------------------------------------------------------------------
     DELETE: bulk
     ------------------------------------------------------------------------- */
  const handleDeleteSelected = async () => {
    const result = await Swal.fire({
      title: 'Delete Selected?',
      text: `Delete ${selectedSuppliers.length} supplier(s)?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      confirmButtonText: 'Yes, Delete',
    });

    if (!result.isConfirmed) return;

    try {
      await Promise.all(
        selectedSuppliers.map((s) =>
          axiosInstance.delete(`/api/suppliers/${s.id}/`, {
            headers: getAuthHeaders(),
          })
        )
      );

      const idsToDelete = selectedSuppliers.map((s) => s.id);
      setSuppliersData((prev) =>
        prev.filter((s) => !idsToDelete.includes(s.id))
      );
      setSelectedSuppliers([]);
      setShowActionDropdown(false);

      Swal.fire({
        icon: 'success',
        title: 'Deleted!',
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error('Bulk delete error:', err?.response?.data || err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Could not delete some suppliers. Please refresh and try again.',
      });
      fetchSuppliers(selectedCity);
    }
  };

  /* -------------------------------------------------------------------------
     Modal — open/reset
     ------------------------------------------------------------------------- */
  const resetForm = () => {
    setFormData({
      title: '', firstName: '', middleName: '', lastName: '', suffix: '',
      companyName: '', supplierDisplayName: '', email: '', phoneNumber: '',
      cc: '', bcc: '', mobileNumber: '', fax: '', other: '',
      streetAddress: '', shop: '', city: '', country: '', postcode: '',
      state: '', balance: '0.00',
    });
    setDisplayNameManuallyEdited(false);
  };

  const handleNewSupplier = () => {
    setIsEditMode(false);
    setCurrentSupplierId(null);
    resetForm();
    setModalCities([]);

    if (currentShopId) {
      setFormData((prev) => ({ ...prev, shop: String(currentShopId) }));
      fetchModalCitiesForShop(currentShopId);
    }
    setIsModalOpen(true);
  };

  const handleEditSupplier = (supplier) => {
    setIsEditMode(true);
    setCurrentSupplierId(supplier.id);
    setFormData({
      title: supplier.title || '',
      firstName: supplier.first_name || '',
      middleName: supplier.middle_name || '',
      lastName: supplier.last_name || '',
      suffix: supplier.suffix || '',
      companyName: supplier.company_name || '',
      supplierDisplayName:
        supplier.supplier_display_name || supplier.supplier || '',
      email: supplier.email === 'N/A' ? '' : supplier.email || '',
      phoneNumber: supplier.phone_number || '',
      cc: supplier.cc || '',
      bcc: supplier.bcc || '',
      mobileNumber: supplier.mobile_number || '',
      fax: supplier.fax || '',
      other: supplier.other || '',
      streetAddress: supplier.street_address || '',
      shop: supplier.shop ? String(supplier.shop) : '',
      city: supplier.city === 'N/A' ? '' : supplier.city || '',
      country: supplier.country || '',
      postcode: supplier.postcode || '',
      state: supplier.state || '',
      balance:
        supplier.open_balance !== undefined
          ? String(supplier.open_balance)
          : '0.00',
    });

    setDisplayNameManuallyEdited(true);

    if (supplier.shop) {
      fetchModalCitiesForShop(supplier.shop);
    } else {
      setModalCities([]);
    }
    setIsModalOpen(true);
  };

  /* -------------------------------------------------------------------------
     Input handling
     ------------------------------------------------------------------------- */
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      if (name === 'companyName' && !displayNameManuallyEdited) {
        return { ...prev, companyName: value, supplierDisplayName: value };
      }
      if (name === 'supplierDisplayName') {
        setDisplayNameManuallyEdited(true);
      }
      return { ...prev, [name]: value };
    });
  };

  const handleModalShopChange = (e) => {
    const shopId = e.target.value;
    setFormData((prev) => ({ ...prev, shop: shopId, city: '' }));
    fetchModalCitiesForShop(shopId);
  };

  /* -------------------------------------------------------------------------
     SAVE: create / update
     POST  /api/suppliers/
     PUT   /api/suppliers/<id>/
     ------------------------------------------------------------------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.supplierDisplayName?.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Info',
        text: 'Please enter a supplier display name.',
      });
      return;
    }

    const payload = {
      title: formData.title || null,
      first_name: formData.firstName || null,
      middle_name: formData.middleName || null,
      last_name: formData.lastName || null,
      suffix: formData.suffix || null,
      company_name: formData.companyName || null,
      supplier_display_name: formData.supplierDisplayName.trim(),
      email: formData.email || null,
      phone_number: formData.phoneNumber || null,
      cc: formData.cc || null,
      bcc: formData.bcc || null,
      mobile_number: formData.mobileNumber || null,
      fax: formData.fax || null,
      other: formData.other || null,
      street_address: formData.streetAddress || null,
      shop: formData.shop ? parseInt(formData.shop, 10) : null,
      city: formData.city || null,
      country: formData.country || null,
      postcode: formData.postcode || null,
      state: formData.state || null,
      open_balance: parseFloat(formData.balance) || 0,
    };

    try {
      setSaving(true);
      const config = { headers: getAuthHeaders() };

      if (isEditMode) {
        await axiosInstance.put(
          `/api/suppliers/${currentSupplierId}/`,
          payload,
          config
        );
        Swal.fire({
          icon: 'success',
          title: 'Updated!',
          timer: 1500,
          showConfirmButton: false,
        });
      } else {
        await axiosInstance.post('/api/suppliers/', payload, config);
        Swal.fire({
          icon: 'success',
          title: 'Added!',
          timer: 1500,
          showConfirmButton: false,
        });
      }

      setIsModalOpen(false);
      fetchSuppliers(selectedCity);
    } catch (err) {
      console.error('Save supplier error:', err?.response?.data || err);

      let errorMsg = 'Something went wrong while saving.';
      if (err?.response?.data) {
        const data = err.response.data;
        if (typeof data === 'object') {
          errorMsg = Object.entries(data)
            .map(([key, val]) => `${key}: ${Array.isArray(val) ? val.join(', ') : val}`)
            .join('\n');
        } else {
          errorMsg = String(data);
        }
      }
      Swal.fire({ icon: 'error', title: 'Error', text: errorMsg });
    } finally {
      setSaving(false);
    }
  };

  /* -------------------------------------------------------------------------
     Click-outside for actions dropdown
     ------------------------------------------------------------------------- */
  const handleClickOutside = (e) => {
    if (
      showActionDropdown &&
      !e.target.closest('.action-dropdown-container')
    ) {
      setShowActionDropdown(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showActionDropdown]);

  /* -------------------------------------------------------------------------
     RENDER
     ------------------------------------------------------------------------- */
  return (
    <div className="suppliers-container">
      {/* 1. TOP ROW */}
      <div className="suppliers-top-row">
        <div className="suppliers-title-area">
          <FaTruck className="suppliers-header-icon" />
          <h2 className="suppliers-header-title">Suppliers</h2>
        </div>

        <div className="suppliers-controls-area">
          <div className="city-dropdown-wrapper">
            <select
              className="city-select-dropdown"
              value={selectedCity}
              onChange={handleCityChange}
              disabled={loadingCities || cities.length === 0}
            >
              <option value="">
                {loadingCities ? 'Loading...' : 'All Cities'}
              </option>
              {cities.map((city, i) => (
                <option key={i} value={city}>
                  {city}
                </option>
              ))}
            </select>
            <IoMdArrowDropdown className="city-dropdown-icon" />
          </div>

          <div className="search-wrapper">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search suppliers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <button className="select-all-btn" onClick={handleSelectAll}>
            <FaCheckDouble />
            <span>
              {selectedSuppliers.length === filteredSuppliers.length &&
              filteredSuppliers.length > 0
                ? 'Deselect All'
                : 'Select All'}
            </span>
          </button>

          <button className="add-user-btn" onClick={handleNewSupplier}>
            <FaPlus />
            <span>NEW SUPPLIER</span>
          </button>
        </div>
      </div>

      {/* 2. MIDDLE ROW */}
      <div className="suppliers-nav-row">
        <div className="nav-right-area">
          <div className="stats-container">
            <div className="stat-card total-suppliers-card">
              <div className="stat-label">Total Suppliers</div>
              <div className="stat-value">{totalSuppliers}</div>
            </div>
            <div className="stat-card overdue-card">
              <div className="stat-label">Overdue</div>
              <div className="stat-value">{formatCurrency(totalOverdue)}</div>
            </div>
            <div className="stat-card openbalance-card">
              <div className="stat-label">Open Balance</div>
              <div className="stat-value">{formatCurrency(totalOpenBalance)}</div>
            </div>
            <div className="stat-card totalpaid-card">
              <div className="stat-label">Total Paid</div>
              <div className="stat-value">{formatCurrency(totalPaid)}</div>
            </div>
          </div>

          <div className="nav-icons">
            <RiBox3Fill className="nav-icon" onClick={() => navigate('/')} />
            <IoSettingsSharp
              className="nav-icon"
              onClick={() => navigate('/settings')}
            />
            <FaCircleUser
              className="nav-icon"
              onClick={() => navigate('/profile')}
            />
          </div>
        </div>
      </div>

      {/* 3. TABLE */}
      <div className="suppliers-content">
        {selectedSuppliers.length > 0 && (
          <div className="selected-summary">
            <div className="summary-content">
              <span className="summary-icon">✓</span>
              <span className="summary-text">
                {selectedSuppliers.length} supplier(s) selected
              </span>
              <div className="action-dropdown-container">
                <button
                  className="action-dropdown-btn"
                  onClick={() => setShowActionDropdown(!showActionDropdown)}
                >
                  <FaEllipsisV /> <span>Actions</span>
                </button>
                {showActionDropdown && (
                  <div className="action-dropdown-menu">
                    <button
                      className="dropdown-item delete-item"
                      onClick={handleDeleteSelected}
                    >
                      <FaTrash /> <span>Delete Selected</span>
                    </button>
                  </div>
                )}
              </div>
              <button
                className="clear-selection"
                onClick={() => setSelectedSuppliers([])}
              >
                Clear all
              </button>
            </div>
          </div>
        )}

        <div className="suppliers-table-container">
          <div className="suppliers-table-wrapper">
            <table className="suppliers-table">
              <thead>
                <tr>
                  <th style={{ width: '50px' }}>
                    <input
                      type="checkbox"
                      checked={
                        selectedSuppliers.length === filteredSuppliers.length &&
                        filteredSuppliers.length > 0
                      }
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th>Supplier</th>
                  <th>Company Name</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>City</th>
                  <th>Open Balance</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan="8"
                      style={{ textAlign: 'center', padding: '2rem' }}
                    >
                      Loading suppliers...
                    </td>
                  </tr>
                ) : filteredSuppliers.length > 0 ? (
                  filteredSuppliers.map((supplier) => (
                    <tr
                      key={supplier.id}
                      className={
                        selectedSuppliers.some((s) => s.id === supplier.id)
                          ? 'selected'
                          : ''
                      }
                    >
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedSuppliers.some(
                            (s) => s.id === supplier.id
                          )}
                          onChange={() => toggleSupplierSelection(supplier)}
                        />
                      </td>
                      <td>
                        <div className="supplier-name-cell">
                          {supplier.supplier}
                        </div>
                      </td>
                      <td>{supplier.companyName}</td>
                      <td>{supplier.phone}</td>
                      <td>{supplier.email}</td>
                      <td>
                        <span className="supplier-city-cell">
                          {supplier.city || 'N/A'}
                        </span>
                      </td>
                      <td>
                        <span className="balance-amount">
                          {formatCurrency(supplier.openBalance)}
                        </span>
                      </td>
                      <td className="actions-cell">
                        <div className="actions-wrapper">
                          <button
                            className="edit-address-btn"
                            onClick={() => handleEditSupplier(supplier)}
                          >
                            <FaEdit />
                          </button>
                          <button
                            className="delete-user-btn"
                            onClick={() => handleDeleteSupplier(supplier)}
                          >
                            <RiDeleteBin5Fill />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="no-users">
                      <FaTruck className="no-data-icon" />
                      <p>No suppliers found.</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* 5. NEW / EDIT MODAL */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div
            className="modal-container qb-style-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2 className="modal-title">
                {isEditMode ? 'Edit Supplier' : 'Supplier'}
              </h2>
              <div className="modal-header-actions">
                <button
                  className="modal-close"
                  onClick={() => setIsModalOpen(false)}
                >
                  ×
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-section no-title">
                <div className="form-row-5">
                  <div className="form-group">
                    <label>Title</label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>First name</label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Middle name</label>
                    <input
                      type="text"
                      name="middleName"
                      value={formData.middleName}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Last name</label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Suffix</label>
                    <input
                      type="text"
                      name="suffix"
                      value={formData.suffix}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="form-row-2">
                  <div className="form-group">
                    <label>Company name</label>
                    <input
                      type="text"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Supplier display name *</label>
                    <input
                      type="text"
                      name="supplierDisplayName"
                      value={formData.supplierDisplayName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-row-2">
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone number</label>
                    <input
                      type="tel"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="form-row-2">
                  <div className="form-group">
                    <label>Cc</label>
                    <input
                      type="text"
                      name="cc"
                      value={formData.cc}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Bcc</label>
                    <input
                      type="text"
                      name="bcc"
                      value={formData.bcc}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="form-row-2">
                  <div className="form-group">
                    <label>Mobile number</label>
                    <input
                      type="tel"
                      name="mobileNumber"
                      value={formData.mobileNumber}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Fax</label>
                    <input
                      type="text"
                      name="fax"
                      value={formData.fax}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="form-row-2">
                  <div className="form-group">
                    <label>Other</label>
                    <input
                      type="text"
                      name="other"
                      value={formData.other}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>

              <div className="form-section no-title address-section">
                <div className="form-group">
                  <label>Street address</label>
                  <textarea
                    name="streetAddress"
                    rows="3"
                    value={formData.streetAddress}
                    onChange={handleInputChange}
                  ></textarea>
                </div>

                <div className="form-row-2 mt-3">
                  <div className="form-group">
                    <label>Shop</label>
                    <select
                      name="shop"
                      value={formData.shop}
                      onChange={handleModalShopChange}
                      disabled={loadingShops}
                      className="qb-select"
                    >
                      <option value="">
                        {loadingShops ? 'Loading shops...' : 'Select a shop'}
                      </option>
                      {shops.map((shop) => (
                        <option key={shop.id} value={shop.id}>
                          {shop.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>City</label>
                    <select
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      disabled={!formData.shop || loadingModalCities}
                      className="qb-select"
                    >
                      <option value="">
                        {loadingModalCities
                          ? 'Loading...'
                          : !formData.shop
                          ? 'Select shop first'
                          : 'Select a city'}
                      </option>
                      {modalCities.map((city) => (
                        <option key={city.id} value={city.name}>
                          {city.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row-2 mt-3">
                  <div className="form-group">
                    <label>Postcode</label>
                    <input
                      type="text"
                      name="postcode"
                      value={formData.postcode}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Country</label>
                    <input
                      type="text"
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
                <div className="form-row-2 mt-3">
                  <div className="form-group">
                    <label>State</label>
                    <input
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Balance (£)</label>
                    <input
                      type="number"
                      name="balance"
                      step="0.01"
                      value={formData.balance}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>

              <div className="modal-actions qb-footer-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setIsModalOpen(false)}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-save qb-btn-save"
                  disabled={saving}
                >
                  {saving
                    ? 'Saving...'
                    : isEditMode
                    ? 'Update'
                    : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Suppliers;